// Cliente HTTP único da aplicação. Os services chamam isto no lugar dos mocks.
// Injeta o token Bearer, normaliza o envelope { success, ... } do backend e
// trata 401 (sessão expirada) emitindo logout global.
import { getToken, clearAuth, emitLogout } from '../lib/auth';

const BASE_URL = (
  process.env.REACT_APP_AGENT_STUDIO_API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:3001'
).replace(/\/$/, '');

const API_PREFIX = (process.env.REACT_APP_AGENT_STUDIO_API_PREFIX || '/agent_studio')
  .replace(/\/$/, '');

function buildUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${BASE_URL}${API_PREFIX}${normalizedPath}`;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Compartilha apenas a requisição em andamento: respostas nunca ficam em cache.
const pendingGets = new Map<string, Promise<unknown>>();

async function request<T>(path: string, options: RequestInit = {}, token = getToken()): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const res = await fetch(buildUrl(path), {
    ...options,
    headers: {
      // Para FormData, o browser define Content-Type (com boundary) automaticamente.
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: token } : {}),
      ...(options.headers ?? {}),
    },
  });

  let body: any = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }

  if (res.status === 401) {
    clearAuth();
    emitLogout();
    throw new ApiError(body?.error ?? 'Sessão expirada. Faça login novamente.', 401);
  }

  if (!res.ok || body?.success === false) {
    throw new ApiError(body?.error ?? `Erro na requisição (${res.status}).`, res.status, body?.code);
  }

  return body as T;
}

function get<T>(path: string): Promise<T> {
  const token = getToken();
  const key = JSON.stringify([token, buildUrl(path)]);
  const existing = pendingGets.get(key);
  if (existing) return existing as Promise<T>;

  const pending = request<T>(path, { method: 'GET' }, token).finally(() => {
    // Uma mutação pode ter criado outra leitura com a mesma chave enquanto esta terminava.
    if (pendingGets.get(key) === pending) pendingGets.delete(key);
  });
  pendingGets.set(key, pending);
  return pending;
}

async function mutate<T>(path: string, options: RequestInit): Promise<T> {
  // Leituras iniciadas antes/durante uma escrita não são reutilizadas depois dela.
  pendingGets.clear();
  try {
    return await request<T>(path, options);
  } finally {
    pendingGets.clear();
  }
}

export const api = {
  get,
  post: <T>(path: string, data?: unknown) =>
    mutate<T>(path, { method: 'POST', body: data != null ? JSON.stringify(data) : undefined }),
  put: <T>(path: string, data?: unknown) =>
    mutate<T>(path, { method: 'PUT', body: data != null ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) =>
    mutate<T>(path, { method: 'PATCH', body: data != null ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => mutate<T>(path, { method: 'DELETE' }),
  // Upload multipart: NÃO define Content-Type (o browser põe o boundary).
  upload: <T>(path: string, form: FormData) => mutate<T>(path, { method: 'POST', body: form }),
  // Baixa um binário (export), com auth. Retorna o Blob.
  async download(path: string): Promise<Blob> {
    const token = getToken();
    const res = await fetch(buildUrl(path), {
      headers: token ? { Authorization: token } : {},
    });
    if (res.status === 401) {
      clearAuth();
      emitLogout();
      throw new ApiError('Sessão expirada.', 401);
    }
    if (!res.ok) throw new ApiError(`Falha no download (${res.status}).`, res.status);
    return res.blob();
  },
};
